'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { DatePickerRootContext } from '../date-picker/context/date-picker-root.context';
import { FormFieldContext } from '../form-field/context/form-field.context';
import { useDateInputState } from './hooks/use-date-input-state';
import { dateInputRecipe } from './styles/date-input.styles';
import type { DateInputPropsInterface } from './types/date-input.types';

/**
 * @since 8.0.0
 */

export const DateInput = forwardRef<HTMLInputElement, DateInputPropsInterface>(
  (props: DateInputPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);
    const datePickerContext = useContext(DatePickerRootContext);

    const {
      className,
      css: cssProp,
      fluid = true,
      size = datePickerContext.size || 'md',
      disabled = formFieldContext.disabled || datePickerContext.disabled || false,
      error = formFieldContext.error || datePickerContext.error || false,
      readOnly = formFieldContext.readOnly || datePickerContext.readOnly || false,
      required = formFieldContext.required || datePickerContext.required || false,
      type = 'text',
      placeholder,
      // Do not use these and let useDateInputState hook manage state
      defaultValue: _defaultValue,
      value: _value,
      onChange: _onChange,
      ...rest
    } = props;

    const styles = css(
      dateInputRecipe.raw({
        size,
        disabled,
        fluid,
        error,
        readOnly,
        hasContext: !!datePickerContext?.datePicker,
      }),
      cssProp,
    );

    const { value, onChange } = useDateInputState({ props, context: datePickerContext });

    return (
      <input
        aria-describedby={formFieldContext.descriptionId}
        aria-invalid={error || undefined}
        ref={forwardedRef}
        className={cx('c11n', styles, className)}
        data-c11n-component="DateInput"
        data-c11n-version={__C11N_VERSION__}
        id={formFieldContext.controlId}
        type={type}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        placeholder={placeholder}
        value={value?.toString()}
        onChange={onChange}
        {...rest}
      />
    );
  },
);

DateInput.displayName = 'DateInput';
